import µ from "../../earth/1.0.0/micro";
import mcMisc from "./mc-overlay-misc"
import mcUtils from "./mc-utils"
import * as THREE from "three";
import * as d3 from "d3";

export default (function() {
    const SEGMENTS = 60; // number of vertices. Higher = better mouse accuracy, slower loading
    const TRANSPARENT_BLACK = [0, 0, 0, 0];     // singleton 0 rgba
    const OVERLAY_ALPHA = Math.floor(0.6*255);  // overlay transparency (on scale [0, 255])
    const earth_radius = 6371000.0; // meter
    const expand_for_simulation = 64.0 // n배 확대

    var log = µ.log();

    // Geometry based lon, lat, projection
    // for examples, X Data Assimilation and Prediction System
    function lonLatGeometry(coords, nLon, nLat, projection, radius){
        let vertices = [];

        for(let y=0; y<nLat; y++){
            for(let x=0; x<nLon; x++){
                vertices.push(projection(coords[x + y*nLon][0], coords[x + y*nLon][1], radius));
            }
        }
        let geometry = new THREE.Geometry();

        // vertices
        for(let i=0; i<vertices.length; i++){
            geometry.vertices.push(new THREE.Vector3(vertices[i][0], vertices[i][1], vertices[i][2]));
        }

        // faces
        for(let y=0; y<nLat - 1; y++){
            for(let x=0; x<nLon - 1; x++){
                geometry.faces.push(new THREE.Face3(y * nLon + x, y * nLon + x + nLon, y * nLon + x + nLon + 1));
                geometry.faces.push(new THREE.Face3(y * nLon + x, y * nLon + x + nLon + 1, y * nLon + x + 1));
            }
        }

        // uvs
        let lonUnit = 1./(nLon - 1);
        let latUnit = 1./(nLat - 1);
        for(let y=0; y<nLat - 1; y++){
            for(let x=0; x<nLon - 1; x++){
                geometry.faceVertexUvs[0].push([
                    new THREE.Vector2(x * lonUnit, y * latUnit),
                    new THREE.Vector2(x * lonUnit, (y + 1) * latUnit),
                    new THREE.Vector2((x + 1) * lonUnit, (y + 1) * latUnit)
                ]);
                geometry.faceVertexUvs[0].push([
                    new THREE.Vector2(x * lonUnit, y * latUnit),
                    new THREE.Vector2((x + 1) * lonUnit, (y + 1) * latUnit),
                    new THREE.Vector2((x + 1) * lonUnit, y * latUnit)
                ]);
            }
        }


        return geometry;
    }

    function lonLatBufferGeometry(coords, nLon, nLat, projection, radius){
        let positions = [];
        let position = [];
        let indices = [];
        let uvs = [];
        let geometry = new THREE.BufferGeometry();

        for(let y=0; y<nLat; y++){
            for(let x=0; x<nLon; x++){
                position = projection(coords[x + y*nLon][0], coords[x + y*nLon][1], radius);
                positions.push(position[0], position[1], position[2]);
            }
        }

        // indices
        for(let y=0; y<nLat - 1; y++){
            for(let x=0; x<nLon - 1; x++){
                indices.push(y * nLon + x, y * nLon + x + nLon, y * nLon + x + nLon + 1);
                indices.push(y * nLon + x, y * nLon + x + nLon + 1, y * nLon + x + 1);
            }
        }

        // uvs
        for(let y=0; y<nLat; y++){
            for(let x=0; x<nLon; x++){
                // uvs.push(x / (nLon - 1), (nLat - y - 1) / (nLat - 1));
                uvs.push(x / (nLon - 1), y / (nLat - 1));
            }
        }
        geometry.setIndex(indices);
        geometry.addAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.addAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        geometry.computeBoundingSphere();

        return geometry;
    }

    function crossSectionPressureLabelPositions(coords, data, pressures, radius, projection){
        let positions = [];
        let position = [];

        const offset = 0; // from 1000hpa ~ to (10hPa - offset'th hPa)
        let nLon = coords.length;      // number of coords
        let nLat = data.length / nLon; // number of pressure(hPa)
        let dLon = 2. * radius / nLon;
        let heightTop = mcUtils.hpaToHeight(pressures[0 + offset]) - mcUtils.hpaToHeight(pressures[nLat - 1]); // 10hPa - 1000hPa
        let py = 0;

        for(let y = 0 + offset; y<nLat; y++){
            if(projection){
                py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
            } else {
                py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
            }

            if(projection){
                position = projection(coords[0][0], coords[0][1], radius + py);
            } else {
                position = [0 * dLon - radius, py - radius / 2, radius];
            }
            positions.push(position);
        }
        return positions;
    }

    function crossSectionGraticuleLabelPositionsInfo(coords, data, pressures, radius, projection){
        let positions = [];
        let position = [];

        const offset = 0; // from 1000hpa ~ to (10hPa - offset'th hPa)
        const offsetZ = 0.; // higher to draw the line than the vertical section face for 2d
        let nLon = coords.length;      // number of coords
        let nLat = data.length / nLon; // number of pressure(hPa)
        let heightTop = mcUtils.hpaToHeight(pressures[0 + offset]) - mcUtils.hpaToHeight(pressures[nLat - 1]); // 10hPa - 1000hPa
        let py = 0;
        let y = 0, x = 0;

        // graticule
        let interpolatedGraticuleCoords = interpolateCoords(coords, 10.);
        let graticuleCoords = interpolatedGraticuleCoords.graticuleCoords;
        let lons = interpolatedGraticuleCoords.lons;

        if(graticuleCoords.length > 0){
            for(let i=0; i<graticuleCoords.length; i++){
                x = interpolatePosition(coords[0], coords[coords.length - 1], graticuleCoords[i][0]);
                y = nLat - 1;
                if(projection){
                    py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
                } else {
                    py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
                }

                if(projection){
                    position = projection(graticuleCoords[i][0], graticuleCoords[i][1], radius + py);
                } else {
                    position = [x * 2 * radius - radius, py - radius / 2, radius + offsetZ];
                }
                positions.push(position);
            }
        }
        return {
            positions: positions,
            lons: lons,
        }
    }

    function abountMidLatCoord(arrays, nLon, nLat){
        let sorted = arrays.filter(function(coord){
            return coord[0] !== 0 && coord[0] !== nLon && coord[1] !== 0 && coord[1] !== nLat;
            // return true;
        }).sort(function(a, b){
            return a[0] - b[0];
        });

        if(sorted.length === 0) return sorted;

        return sorted[Math.floor(sorted.length * 0.5)];
    }

    function crossSectionContourLabelInfo(contours, nLon, nLat, pressures, radius) {
        let heightTop = mcUtils.hpaToHeight(pressures[0]) - mcUtils.hpaToHeight(pressures[nLat - 1]); // 10hPa - 1000hPa
        let dLon = 2. * radius / nLon;
        let py = 0;
        let x = 0;
        let y = 0;
        let pressureHeight = 0;
        let pressureHeight0 = 0;
        let pressureHeight1 = 0;

        let positions = [];
        let values = [];
        let labelingCoord = [0, 0];

        contours.map(function(polygon){
            polygon.coordinates.forEach(function(lines) {
                lines.forEach(function(line){
                    labelingCoord = abountMidLatCoord(line.slice(0), nLon, nLat);

                    if(labelingCoord.length !== 0) {
                        x = labelingCoord[0] - 0.5;
                        y = labelingCoord[1] - 0.5;

                        if(x === -0.5){
                            x = -0.1;
                        } else if(x === (nLon - 0.5)){
                            x = nLon - 0.9;
                        } else if(y === -0.5){
                            pressureHeight = mcUtils.hpaToHeight(pressures[0]) + 0.1;
                        } else if(y === (nLat - 0.5)){
                            pressureHeight = mcUtils.hpaToHeight(pressures[nLat - 1]) - 0.1;
                        } else if(y === (nLat - 1) || y === 0){
                            pressureHeight = mcUtils.hpaToHeight(pressures[y]);
                        } else {
                            pressureHeight0 = mcUtils.hpaToHeight(pressures[Math.floor(y)]);
                            pressureHeight1 = mcUtils.hpaToHeight(pressures[Math.floor(y) + 1]);
                            pressureHeight = pressureHeight0 - (pressureHeight0 - pressureHeight1) * (y - Math.floor(y));
                        }
                        py = (pressureHeight - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;

                        positions.push([x * dLon - radius, (py - radius / 2), radius]);
                        values.push(polygon.value);
                    }
                });
            });
        });

        return {
            positions: positions,
            values: values,
        }
    }

    function crossSectionContourGeometry(contours, nLon, nLat, pressures, radius) {
        let heightTop = mcUtils.hpaToHeight(pressures[0]) - mcUtils.hpaToHeight(pressures[nLat - 1]); // 10hPa - 1000hPa
        let dLon = 2. * radius / nLon;
        let py = 0;
        let x = 0;
        let y = 0;
        let pressureHeight = 0;
        let pressureHeight0 = 0;
        let pressureHeight1 = 0;

        let geometry = new THREE.Geometry();

        contours.map(function(polygon){
            polygon.coordinates.forEach(function(lines) {
                lines.forEach(function(line){
                    d3.pairs(line.map(function(coord){
                        x = coord[0] - 0.5;
                        y = coord[1] - 0.5;

                        if(x === -0.5){
                            x = -0.1;
                        } else if(x === (nLon - 0.5)){
                            x = nLon - 0.9;
                        } else if(y === -0.5){
                            pressureHeight = mcUtils.hpaToHeight(pressures[0]) + 0.1;
                        } else if(y === (nLat - 0.5)){
                            pressureHeight = mcUtils.hpaToHeight(pressures[nLat - 1]) - 0.1;
                        } else if(y === (nLat - 1) || y === 0){
                            pressureHeight = mcUtils.hpaToHeight(pressures[y]);
                        } else {
                            pressureHeight0 = mcUtils.hpaToHeight(pressures[Math.floor(y)]);
                            pressureHeight1 = mcUtils.hpaToHeight(pressures[Math.floor(y) + 1]);
                            pressureHeight = pressureHeight0 - (pressureHeight0 - pressureHeight1) * (y - Math.floor(y));
                        }
                        py = (pressureHeight - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
                        return new THREE.Vector3(
                            x * dLon - radius,
                            (py - radius / 2),
                            radius
                        );
                    }), function(a, b) {
                        geometry.vertices.push(a, b);
                    });
                });
            });
        });

        return geometry;
    }

    function crossSectionContour(nLon, nLat, data, type){
        return mcMisc.contour(nLon, nLat, data, type);
    }

    function crossSectionGeometry(coords, data, pressures, radius, scale, binaryScale, projection){
        let positions = [];
        var normals = [];
        let position = [];
        let colors = [];
        let indices = [];
        let geometry = new THREE.BufferGeometry();
        const earth_radius = 6371000.0; // meter
        const expand_for_simulation = 64.0 // n배 확대

        const offset = 0; // from 1000hpa ~ to (10hPa - offset'th hPa)
        let nLon = coords.length;      // number of coords
        let nLat = data.length / nLon; // number of pressure(hPa)
        let dLon = 2. * radius / nLon;
        // let dLat = 2. * (radius / 2) / nLat;
        let heightTop = mcUtils.hpaToHeight(pressures[0 + offset]) - mcUtils.hpaToHeight(pressures[nLat - 1]); // 10hPa - 1000hPa
        // console.log("heightTop", heightTop);
        let py = 0;
        let scalar = null;
        let color = TRANSPARENT_BLACK;

        for(let y = 0 + offset; y<nLat; y++){
            if(projection){
                py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
            } else {
                py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
            }
            // console.log("pressure, height", pressures[y], mcUtils.hpaToHeight(pressures[y]));
            for(let x=0; x<nLon; x++){
                if(projection){
                    position = projection(coords[x][0], coords[x][1], radius + py);
                } else {
                    position = [x * dLon - radius, py - radius / 2, radius];
                }
                positions.push(position[0], position[1], position[2]);
                normals.push( 0, 0, 1 );
                scalar = projection ? data[x + (nLat - y - 1) * nLon] : data[x + y * nLon];
                color = scale.gradient(scalar[2] / binaryScale, OVERLAY_ALPHA);
                colors.push(color[0] / 255., color[1] / 255., color[2] / 255., color[3] / 255.);
            }
        }

        for(let x=0; x<nLon - 1; x++){
            for(let y=0; y<nLat - 1 - offset; y++){
                indices.push(y * nLon + x + 1, y * nLon + x, y * nLon + x + nLon + 1);
                indices.push(y * nLon + x, y * nLon + x + nLon, y * nLon + x + nLon + 1);
            }
        }

        // for(let y=0; y<nLat - 1 - offset; y++){
        //     for(let x=0; x<nLon - 1; x++){
        //         if(x === 0 && y > 0){
        //             indices.push(y * nLon + x);
        //         }
        //         indices.push(y * nLon + x, y * nLon + x + nLon, y * nLon + x + 1);
        //         if(x === nLon - 2){
        //             indices.push(y * nLon + x + nLon + 1);
        //             if(y < nLat - 2){
        //                 indices.push(y * nLon + x + nLon + 1);
        //             }
        //         }
        //     }
        // }
        geometry.setIndex(indices);
        geometry.addAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.addAttribute( 'normal', new THREE.Float32BufferAttribute( normals, 3 ) );
        geometry.addAttribute('color', new THREE.Float32BufferAttribute(colors, 4));
        geometry.computeBoundingSphere();

        return geometry;
    }

    function crossSectionPressureLineGeometry(coords, data, pressures, radius, projection){
        let positions = [];
        let position = [];
        let geometry = new THREE.BufferGeometry();
        const earth_radius = 6371000.0; // meter
        const expand_for_simulation = 64.0 // n배 확대

        const offset = 0; // from 1000hpa ~ to (10hPa - offset'th hPa)
        const offsetZ = 0.01; // higher to draw the line than the vertical section face for 2d
        let nLon = coords.length;      // number of coords
        let nLat = data.length / nLon; // number of pressure(hPa)
        let dLon = 2. * radius / nLon;
        let heightTop = mcUtils.hpaToHeight(pressures[0 + offset]) - mcUtils.hpaToHeight(pressures[nLat - 1]); // 10hPa - 1000hPa
        let py = 0;

        // pressure
        for(let y = 0 + offset; y<nLat; y++){
            if(projection){
                py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
            } else {
                py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
            }
            for(let x=0; x<nLon-1; x++){
                if(projection){
                    position = projection(coords[x][0], coords[x][1], radius + py);
                } else {
                    position = [x * dLon - radius, py - radius / 2, radius + offsetZ];
                }
                positions.push(position[0], position[1], position[2]);

                if(projection){
                    position = projection(coords[x + 1][0], coords[x + 1][1], radius + py);
                } else {
                    position = [(x + 1) * dLon - radius, py - radius / 2, radius + offsetZ];
                }
                positions.push(position[0], position[1], position[2]);
            }
        }

        // left edge
        let y = 0, x = 0;
        if(projection){
            py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
        } else {
            py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
        }

        if(projection){
            position = projection(coords[x][0], coords[x][1], radius + py);
        } else {
            position = [x * dLon - radius, py - radius / 2, radius + offsetZ];
        }
        positions.push(position[0], position[1], position[2]);

        y = nLat - 1;
        if(projection){
            py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
        } else {
            py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
        }

        if(projection){
            position = projection(coords[x][0], coords[x][1], radius + py);
        } else {
            position = [x * dLon - radius, py - radius / 2, radius + offsetZ];
        }
        positions.push(position[0], position[1], position[2]);

        // right edge
        y = 0;
        x = nLon - 1;
        if(projection){
            py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
        } else {
            py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
        }

        if(projection){
            position = projection(coords[x][0], coords[x][1], radius + py);
        } else {
            position = [x * dLon - radius, py - radius / 2, radius + offsetZ];
        }
        positions.push(position[0], position[1], position[2]);

        y = nLat - 1;
        if(projection){
            py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
        } else {
            py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
        }

        if(projection){
            position = projection(coords[x][0], coords[x][1], radius + py);
        } else {
            position = [x * dLon - radius, py - radius / 2, radius + offsetZ];
        }
        positions.push(position[0], position[1], position[2]);

        // graticule
        let graticuleCoords = interpolateCoords(coords, 10.).graticuleCoords;
        if(graticuleCoords.length > 0){
            for(let i=0; i<graticuleCoords.length; i++){
                y = 0;
                x = interpolatePosition(coords[0], coords[coords.length - 1], graticuleCoords[i][0]);
                if(projection){
                    py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
                } else {
                    py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
                }

                if(projection){
                    position = projection(graticuleCoords[i][0], graticuleCoords[i][1], radius + py);
                } else {
                    position = [x * 2 * radius - radius, py - radius / 2, radius + offsetZ];
                }
                positions.push(position[0], position[1], position[2]);

                y = nLat - 1;
                if(projection){
                    py = (mcUtils.hpaToHeight(pressures[nLat - y - 1]) - mcUtils.hpaToHeight(pressures[nLat - 1])) * radius / earth_radius * expand_for_simulation;
                } else {
                    py = (mcUtils.hpaToHeight(pressures[y]) - mcUtils.hpaToHeight(pressures[nLat - 1])) / heightTop * radius;
                }

                if(projection){
                    position = projection(graticuleCoords[i][0], graticuleCoords[i][1], radius + py);
                } else {
                    position = [x * 2 * radius - radius, py - radius / 2, radius + offsetZ];
                }
                positions.push(position[0], position[1], position[2]);
            }
        }

        geometry.addAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

        return geometry;
    }

    function interpolatePosition(coord1, coord2, lon){
        let coord1Lon = coord1[0] > 0 ? coord1[0] : coord1[0] + 360;
        let coord2Lon = coord2[0] > 0 ? coord2[0] : coord2[0] + 360;
        lon = lon > 0 ? lon : lon + 360;

        let crossSectionLonWidth = 1;
        let positionLonWidth = 0;

        if(coord1Lon < coord2Lon){
            crossSectionLonWidth = coord2Lon - coord1Lon;
        } else {
            crossSectionLonWidth = 360 - coord1Lon + coord2Lon;
        }

        if(coord1Lon < lon){
            positionLonWidth = lon - coord1Lon;
        } else {
            positionLonWidth = 360 - coord1Lon + lon;
        }

        return positionLonWidth / crossSectionLonWidth;
    }

    function calcProportionedCoord(coord1, coord2, lon){
        let ratio = (coord1[0] - lon) / (coord1[0] - coord2[1]);
        let lat = coord1[1] + (coord1[1] - coord2[1]) * ratio;
        return [lon, lat];
    }

    function interpolateCoords(coords, ratio){
        let startCoord = coords[0];
        let endCoord = coords[coords.length - 1];
        let startLon = startCoord[0];
        let endLon = endCoord[0];
        startLon = Math.floor(startLon / ratio);
        endLon = Math.floor(endLon / ratio);
        let lons = [];
        let resultLons = [];
        let graticuleCoords = [];

        const fullLons = [];
        let startIndex = 0;

        for(let lll = 0; lll <= 18; lll++){
            fullLons.push(lll);
        }

        for(let lll = -18; lll < 0; lll++){
            fullLons.push(lll);
        }

        for(let lll = 0; lll < fullLons.length; lll++){
            if(fullLons[lll] === startLon) break;
            startIndex++;
        }

        if (startLon === endLon){
            for(let lll = startIndex; lll <= fullLons.length + startIndex; lll++){
                lons.push(fullLons[lll % fullLons.length] * ratio);
            }
        } else {
            for(let lll = startIndex; lll < fullLons.length + startIndex; lll++){
                lons.push(fullLons[lll % fullLons.length] * ratio);
                if(fullLons[lll % fullLons.length] === endLon) break;
            }

        }

        lons = lons.slice(1);

        for(let i = 0; i<lons.length; i++){
            if(i > 0 && (lons[i - 1] === 180.) && (lons[i] === -180.)){
                // skip
            } else {
                resultLons.push(lons[i]);
            }
            for(let j = 0; j<coords.length - 1; j++){
                if(lons[i] === 180.){
                    if(coords[j][0] > 0. && coords[j + 1][0] < 0. && coords[j][0] < lons[i] && lons[i] < (360 - coords[j + 1][0])){
                        graticuleCoords.push(calcProportionedCoord(coords[j], coords[j + 1], lons[i]));
                    }
                }
                if(coords[j][0] <= lons[i] && lons[i] <= coords[j + 1][0]){
                    graticuleCoords.push(calcProportionedCoord(coords[j], coords[j + 1], lons[i]));
                }
            }
        }

        return {
            graticuleCoords: graticuleCoords,
            lons: resultLons,
        };
    }

    function sphereBufferGeometry(coords, colors, nLon, nLat, projection, radius){
        let positions = [];
        let position = [];
        let indices = [];
        let geometry = new THREE.BufferGeometry();

        for(let y=0; y<nLat; y++){
            for(let x=0; x<nLon; x++){
                position = projection(coords[y * nLon + x][0], coords[y * nLon + x][1], radius);
                positions.push(position[0], position[1], position[2]);

                if(x < nLon - 1 && y < nLat - 1){
                    indices.push(y * nLon + x, y * nLon + x + nLon, y * nLon + x + nLon + 1);
                    indices.push(y * nLon + x, y * nLon + x + nLon + 1, y * nLon + x + 1);

                    // indices.push(y * nLon + x + 1, y * nLon + x, y * nLon + x + nLon + 1);
                    // indices.push(y * nLon + x, y * nLon + x + nLon, y * nLon + x + nLon + 1);
                }
            }
        }
        geometry.setIndex(indices);
        geometry.addAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.addAttribute('color', new THREE.Float32BufferAttribute(colors, 4));
        geometry.computeBoundingSphere();

        return geometry;
    }

    function sphereBufferGeometryTheree(radius){
        return new THREE.SphereBufferGeometry(radius, SEGMENTS, SEGMENTS);
    }

    function interpolateCoordsByRatio(coords, symbol){
        let ratioLon = 6.;
        let ratioLat = 3.;
        const startLon = coords[0][0] < 0 ? (180 + coords[0][0]) + 180 : coords[0][0];
        const endLon = coords[0][1] < 0 ? (180 + coords[0][1]) + 180 : coords[0][1];
        const startLat = coords[1][0];
        const endLat = coords[1][1];

        if(symbol){
            ratioLon = (endLon - startLon) / 2.;
            ratioLat = (startLat - endLat) / 2.;
        }

        let targetStartLon = startLon;
        let targetStartLat = startLat;
        let targetLons = [];
        let targetLats = [];

        for(let lon = 0; lon < 360; lon += ratioLon){
            targetStartLon = lon;
            if(lon > startLon) break;
        }

        targetLons.push(startLon);

        for(let lon = targetStartLon; lon < endLon; lon += ratioLon){
            targetLons.push(lon);
        }

        targetLons.push(endLon);

        for(let lat = 90; lat > -90; lat -= ratioLat){
            targetStartLat = lat;
            if(lat < startLat) break;
        }

        targetLats.push(startLat);

        for(let lat = targetStartLat; lat > endLat; lat -= ratioLat){
            targetLats.push(lat);
        }

        targetLats.push(endLat);

        return [targetLons, targetLats];
    }

    function planeGeometry(coords, color, radius, projection, symbol){
        const interpolatedCoords = interpolateCoordsByRatio(coords, symbol);

        let positions = [];
        let position = [];
        let indices = [];
        let colors = [];
        let uvs = [];
        let geometry = new THREE.BufferGeometry();

        const nLon = interpolatedCoords[0].length;
        const nLat = interpolatedCoords[1].length;

        for(let y=0; y<nLat; y++){
            for(let x=0; x<nLon; x++){
                position = projection(interpolatedCoords[0][x], interpolatedCoords[1][y], radius);
                positions.push(position[0], position[1], position[2]);

                colors.push(color[0], color[1], color[2], color[3]);

                if(x < nLon - 1 && y < nLat - 1){
                    indices.push(y * nLon + x, y * nLon + x + nLon, y * nLon + x + nLon + 1);
                    indices.push(y * nLon + x, y * nLon + x + nLon + 1, y * nLon + x + 1);

                    // indices.push(y * nLon + x + 1, y * nLon + x, y * nLon + x + nLon + 1);
                    // indices.push(y * nLon + x, y * nLon + x + nLon, y * nLon + x + nLon + 1);
                }

                // uvs.push(x / (nLon - 1), y / (nLat - 1));
                uvs.push(x / (nLon - 1), (nLat - y - 1) / (nLat - 1));
            }
        }

        geometry.setIndex(indices);
        geometry.addAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.addAttribute('color', new THREE.Float32BufferAttribute(colors, 4));
        geometry.addAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        geometry.computeBoundingSphere();

        return geometry;
    }

    function planeGeometryPairs(coords, radius, projection) {
        const interpolatedCoords = interpolateCoordsByRatio(coords);
        const nLon = interpolatedCoords[0].length;
        const nLat = interpolatedCoords[1].length;

        let positions = [];
        let position = [];

        // ---------
        for(let x=0; x<nLon; x++){
            position = projection(interpolatedCoords[0][x], interpolatedCoords[1][0], radius);
            positions.push(new THREE.Vector3(
                position[0],
                position[1],
                position[2]
            ));
        }

        // ---------
        //         |
        //         |
        for(let y=0; y<nLat; y++){
            position = projection(interpolatedCoords[0][nLon - 1], interpolatedCoords[1][y], radius);
            positions.push(new THREE.Vector3(
                position[0],
                position[1],
                position[2]
            ));
        }

        // ---------
        //         |
        //         |
        // --------
        for(let x = nLon - 1; x >= 0; x--){
            position = projection(interpolatedCoords[0][x], interpolatedCoords[1][nLat - 1], radius);
            positions.push(new THREE.Vector3(
                position[0],
                position[1],
                position[2]
            ));
        }

        // ---------
        // |       |
        // |       |
        // --------
        for(let y = nLat - 1; y >= 0; y--){
            position = projection(interpolatedCoords[0][0], interpolatedCoords[1][y], radius);
            positions.push(new THREE.Vector3(
                position[0],
                position[1],
                position[2]
            ));
        }

        let geometry = new THREE.Geometry();

        d3.pairs(positions, function(a, b) {
            geometry.vertices.push(a, b);
        });

        return geometry;
    }

    return {
        lonLatGeometry: lonLatGeometry,
        lonLatBufferGeometry: lonLatBufferGeometry,
        sphereBufferGeometry: sphereBufferGeometry,
        sphereBufferGeometryTheree: sphereBufferGeometryTheree,
        crossSectionPressureLabelPositions: crossSectionPressureLabelPositions,
        crossSectionGraticuleLabelPositionsInfo: crossSectionGraticuleLabelPositionsInfo,
        crossSectionGeometry: crossSectionGeometry,
        crossSectionPressureLineGeometry: crossSectionPressureLineGeometry,
        crossSectionContour: crossSectionContour,
        crossSectionContourGeometry: crossSectionContourGeometry,
        crossSectionContourLabelInfo: crossSectionContourLabelInfo,
        planeGeometry: planeGeometry,
        planeGeometryPairs: planeGeometryPairs,
    };

})();